/**
 * cart-promo-drawer.js
 * Promo-code-only drawer for guests (non-members).
 *
 * Sibling of cart-coupon-drawer.js — kept SEPARATE so the member coupon/points
 * drawer (window.CartCoupon) stays untouched. This module only knows about a
 * single "Enter Promo Code" input and applies codes straight to the Shopify cart.
 *
 * Exposed as:  window.CartPromo
 * Backward-compat globals: openPromoDrawer(), closePromoDrawer(), addPromoCode()
 *
 * Why not CartLockManager.lockCoupon()? That requires a logged-in customerId
 * (CRM member coupon lock) and returns false for guests. Guest promo codes are
 * plain Shopify discount codes, so we apply them directly through
 * CartLockManager.syncShopifyDiscounts() (Storefront cartDiscountCodesUpdate),
 * which needs only the storefront token + AJAX cart token — both present for guests.
 *
 * Depends on:  window.CartLockManager (cart-lock-manager.js) — syncShopifyDiscounts
 *              window.CartUtils       (cart-timer.js)        — refreshCart
 */

(function (global) {
  'use strict';

  /* ─── Localized strings (loaded from #promo-drawer data-attrs; English defaults) ─ */
  var _STR = {
    added:   'Added',
    remove:  'Remove [code]',
    invalid: 'This promo code is invalid or not applicable.',
  };
  function _loadStrings() {
    var d = document.getElementById('promo-drawer');
    if (!d) return;
    if (d.dataset.strAdded)   _STR.added   = d.dataset.strAdded;
    if (d.dataset.strRemove)  _STR.remove  = d.dataset.strRemove;
    if (d.dataset.strInvalid) _STR.invalid = d.dataset.strInvalid;
  }

  /* ─── State ──────────────────────────────────────────────────────────────── */
  var _applied  = [];      // array of promo code strings currently active on the cart
  var _isBusy   = false;

  /* ─── DOM helpers ────────────────────────────────────────────────────────── */
  function _el(sel) { return document.querySelector(sel); }

  /* ─── Drawer open / close ────────────────────────────────────────────────── */
  function open() {
    var drawer  = document.getElementById('promo-drawer');
    var overlay = document.getElementById('promo-drawer-overlay');
    if (!drawer || !overlay) return;
    drawer.classList.add('is-open');
    overlay.classList.add('is-open');
    document.documentElement.style.overflow = 'hidden';
    document.body.style.overflow = 'hidden';
  }

  function close() {
    var drawer  = document.getElementById('promo-drawer');
    var overlay = document.getElementById('promo-drawer-overlay');
    if (drawer)  drawer.classList.remove('is-open');
    if (overlay) overlay.classList.remove('is-open');
    document.documentElement.style.overflow = '';
    document.body.style.overflow = '';
  }

  /* ─── Error message toggle ───────────────────────────────────────────────── */
  function _showError(show) {
    var errorEl = _el('#promo-drawer .coupon-code-error');
    var input   = document.getElementById('promo-code-input');
    if (errorEl) errorEl.style.display = show ? 'block' : 'none';
    if (input)   input.classList.toggle('is-error', !!show);
  }

  /* ─── Add / apply a typed promo code ─────────────────────────────────────── */
  // Guest promo codes are plain Shopify discount codes. syncShopifyDiscounts()
  // does a FULL replacement, so we always send the union of already-applied codes
  // plus the new one, then reconcile against the codes Shopify rejected.
  async function addCode() {
    if (_isBusy) return;
    var input = document.getElementById('promo-code-input');
    if (!input || !global.CartLockManager) return;

    var code = input.value.trim().toUpperCase();
    if (!code) return;

    // Already applied — no-op
    if (_applied.indexOf(code) !== -1) { input.value = ''; return; }

    var addBtn     = _el('#promo-drawer .coupon-drawer__add-btn');
    var addBtnText = addBtn ? addBtn.textContent : '';
    if (addBtn) { addBtn.disabled = true; addBtn.textContent = _STR.added; }
    _showError(false);

    _isBusy = true;
    try {
      var desired      = _applied.concat([code]);
      var notApplicable = await global.CartLockManager.syncShopifyDiscounts(desired);
      // syncShopifyDiscounts returns [] on network failure too — treat the code as
      // rejected only when it is explicitly listed as not applicable.
      var rejected = Array.isArray(notApplicable) &&
        notApplicable.map(function (c) { return String(c).toUpperCase(); }).indexOf(code) !== -1;

      if (rejected) {
        _showError(true);
      } else {
        _applied.push(code);
        input.value = '';
        _renderTags();
        if (global.CartUtils) await global.CartUtils.refreshCart();
        close();
        document.dispatchEvent(new CustomEvent('cart:promo-applied', { detail: _applied.slice() }));
      }
    } finally {
      _isBusy = false;
      if (addBtn) { addBtn.disabled = false; addBtn.textContent = addBtnText; }
    }
  }

  /* ─── Remove a single promo code (tag × button) ──────────────────────────── */
  async function _removeOne(code) {
    if (_isBusy || !global.CartLockManager) return;
    _isBusy = true;
    try {
      var remaining = _applied.filter(function (c) { return c !== code; });
      await global.CartLockManager.syncShopifyDiscounts(remaining);
      _applied = remaining;
      _renderTags();
      if (global.CartUtils) await global.CartUtils.refreshCart();
      document.dispatchEvent(new CustomEvent('cart:promo-removed', { detail: { code: code } }));
    } finally {
      _isBusy = false;
    }
  }

  /* ─── Render removable tags ──────────────────────────────────────────────── */
  function _renderTags() {
    var container = _el('[data-promo-tags]');
    if (!container) return;
    container.innerHTML = '';
    _applied.forEach(function (code) {
      var tag = document.createElement('div');
      tag.className = 'cart-applied-tag';
      tag.setAttribute('data-promo-tag', code);
      tag.innerHTML =
        '<p class="p3">' + code + '</p>' +
        '<button type="button" class="cart-applied-tag__remove" aria-label="' +
          _STR.remove.replace('[code]', code) + '">&#215;</button>';
      tag.querySelector('button').addEventListener('click', function () {
        _removeOne(code);
      });
      container.appendChild(tag);
    });
  }

  /* ─── Restore already-applied discount codes on page load ────────────────── */
  // Seed _applied from the live cart so a code applied earlier still shows a
  // removable tag. /cart.js exposes discount_codes: [{ code, applicable }].
  async function _restore() {
    try {
      var res = await fetch('/cart.js');
      if (!res.ok) return;
      var cart = await res.json();
      var codes = (cart && cart.discount_codes) || [];
      _applied = codes
        .filter(function (dc) { return dc && dc.code && dc.code.indexOf('POINTS') !== 0; })
        .map(function (dc) { return String(dc.code).toUpperCase(); });
      _renderTags();
    } catch (e) {
      /* non-fatal — drawer still works for new entries */
    }
  }

  /* ─── Boot ───────────────────────────────────────────────────────────────── */
  document.addEventListener('DOMContentLoaded', function () {
    if (!document.getElementById('promo-drawer')) return;
    _loadStrings();
    _restore();

    // Enter key in the input triggers Add
    var input = document.getElementById('promo-code-input');
    if (input) {
      input.addEventListener('keydown', function (e) {
        if (e.key === 'Enter') { e.preventDefault(); addCode(); }
      });
    }
  });

  /* ─── Exports ────────────────────────────────────────────────────────────── */
  global.CartPromo = {
    open:       open,
    close:      close,
    addCode:    addCode,
    getApplied: function () { return _applied.slice(); },
    hasApplied: function () { return _applied.length > 0; },
  };

  global.openPromoDrawer  = open;
  global.closePromoDrawer = close;
  global.addPromoCode     = addCode;

})(window);
